import { Fragment, useState, useEffect, useRef, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import ModalNew from "../Modal";
import PopupHeader from "../common/popupHeader";
import PopupContent from "../common/PopupContent";
import AddressFields from "../../forms/address/AddressFields";
import LoadingPlaceholder from "../../loading/LoadingPlaceholder";
import SmartButton from "../../buttons/SmartButton";
import { addAddressDate } from "../../../../utils/forms/address/addressHelpers";
import useGoogleAddressValidation from "../../../../utils/forms/address/useGoogleAddressValidation";
import AddressSuggestionPreview from "../../../../utils/forms/address/AddressSuggestionPreview";
import { addressSchema, singleAddressSchema } from "../../../../utils/validation/addressValidation";

function AddressPopup({
  editingIndex,
  setCurrentAddress,
  currentAddress,
  setIsModalOpen,
  handleSave,
  isMulti = true,
}) {
  const title = editingIndex === null ? "Add Address" : "Edit Address";
  const [step, setStep] = useState("initial"); // initial, validating, review
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const {
    userAddress,
    suggestedAddress,
    validateAndMaybeSuggest,
  } = useGoogleAddressValidation();
  const [buttonState, setButtonState] = useState("default");
  const [localUserAddress, setLocalUserAddress] = useState(null);
  const [localSuggestedAddress, setLocalSuggestedAddress] = useState(null);
  const hasInitialized = useRef(false);

  // Initialize react-hook-form with validation
  const formMethods = useForm({
    resolver: zodResolver(isMulti ? addressSchema : singleAddressSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      address: "",
      suiteAddress: "",
      city: "",
      stateAddress: "",
      zipCode: "",
      country: "US",
      ...(isMulti ? { fromDate: "", toDate: "" } : {}),
      ...currentAddress,
      ...(editingIndex === null ? { stateAddress: "" } : {}),
    },
  });

  const {
    formState: { isValid },
    watch,
    trigger,
  } = formMethods;

  // Watch form values and sync with currentAddress
  const watchedValues = watch();

  // Use useCallback to stabilize setCurrentAddress
  const updateCurrentAddress = useCallback(
    (newValues) => {
      setCurrentAddress((prev) => {
        // Only update if the values are actually different to avoid infinite loops
        const hasChanges = Object.keys(newValues).some(
          (key) => newValues[key] !== prev[key]
        );
        return hasChanges ? newValues : prev;
      });
    },
    [setCurrentAddress]
  );

  useEffect(() => {
    if (watchedValues && Object.keys(watchedValues).length > 0) {
      updateCurrentAddress(watchedValues);
    }
  }, [watchedValues, updateCurrentAddress]);

  // Reset form when currentAddress changes from outside (only on initial load)
  useEffect(() => {
    if (
      currentAddress &&
      Object.keys(currentAddress).length > 0 &&
      !hasInitialized.current
    ) {
      // For new addresses, ensure stateAddress is empty initially
      const resetValues =
        editingIndex === null
          ? { ...currentAddress, stateAddress: "" }
          : currentAddress;
      formMethods.reset(resetValues, {
        keepErrors: false, // Don't keep errors when resetting - let validation work normally
        keepDirty: false, // Reset dirty state
        keepIsSubmitted: false, // Reset submitted state
      });
      hasInitialized.current = true;
    }
  }, [currentAddress, formMethods, editingIndex]);

  // Watch for suggestedAddress from the hook to update local state
  useEffect(() => {
    if (step === "validating" && suggestedAddress && userAddress) {
      setLocalSuggestedAddress(suggestedAddress);
      setLocalUserAddress(userAddress);
      setStep("review");
    }
  }, [suggestedAddress, userAddress, step]);

  const handleValidate = async () => {
    // Trigger validation first
    const isValidForm = await trigger();

    if (!isValidForm) {
      setButtonState("error");
      // Focus on the first field with error
      const firstErrorField = Object.keys(formMethods.formState.errors)[0];
      if (firstErrorField) {
        const field = document.querySelector(`[name="${firstErrorField}"]`);
        if (field) {
          field.focus();
          field.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
      return;
    }

    setStep("validating");
    setButtonState("default");
    setLocalUserAddress(currentAddress);
    setLocalSuggestedAddress(null); // Reset previous suggestion

    try {
      const hasSuggestion = await validateAndMaybeSuggest(currentAddress, { alwaysReview: true });
      
      // If validation returns true, addresses are equivalent or no suggestion, save directly
      if (hasSuggestion) {
        const finalAddress = isMulti
          ? addAddressDate(currentAddress, currentAddress.fromDate, currentAddress.toDate)
          : currentAddress;
        handleSave(finalAddress);
      }
      // If hasSuggestion is false, the useEffect will handle showing the review step
    } catch (error) {
      setStep("initial");
      setButtonState("error");
    }
  };

  const handleSelect = (choice) => {
    const finalAddress = isMulti
      ? addAddressDate(choice, currentAddress.fromDate, currentAddress.toDate)
      : choice;
    handleSave(finalAddress);
  };

  return (
    <Fragment>
      <ModalNew className="content-modal" style={{ maxWidth: "480px", display: isDatePickerOpen ? "none" : undefined }}>
        <PopupHeader 
          title={title} 
          setOpenState={setIsModalOpen}
          icon={`${process.env.PUBLIC_URL}/images/icons/home_blue_popup.svg`}
          variant="compact"
        />
        <PopupContent>
          {/**
           * Initial state, once the component is rendered.
           */}
          {step === "initial" && (
            <div style={{ display: "grid", gap: "20px" }}>
              <AddressFields
                formState={currentAddress}
                setFormState={setCurrentAddress}
                isMulti={isMulti}
                fieldNames={{
                  address: "address",
                  suiteAddress: "suiteAddress",
                  city: "city",
                  stateAddress: "stateAddress",
                  zipCode: "zipCode",
                }}
                formMethods={formMethods}
                onDatePickerOpen={() => setIsDatePickerOpen(true)}
                onDatePickerClose={() => setIsDatePickerOpen(false)}
              />
              <div className="modal-actions" style={{ justifyContent: 'center' }}>
                <SmartButton
                  text="Save"
                  loadingText="Validating..."
                  successText="Validated!"
                  state={buttonState}
                  isSubmitting={step === "validating"}
                  onClick={handleValidate}
                  type="button"
                  icon="save"
                  disabled={!isValid || step === "validating"}
                />
              </div>
            </div>
          )}

          {/**
           * Waiting for API validation response
           */}
          {step === "validating" && (
            <div style={{ display: "grid", gap: "20px" }}>
              <LoadingPlaceholder rows={1} type="line" />
              <LoadingPlaceholder rows={1} type="card-large" />
            </div>
          )}

          {/**
           * Review state - show recommendation vs user entered
           */}
          {step === "review" && localSuggestedAddress && (
            <div style={{ display: "grid", gap: "20px" }}>
              <AddressSuggestionPreview
                userAddress={localUserAddress || currentAddress}
                suggestedAddress={localSuggestedAddress}
                handleSelect={handleSelect}
              />
            </div>
          )}
        </PopupContent>
      </ModalNew>
    </Fragment>
  );
}

export default AddressPopup;
